import { createPinia } from 'pinia';
import { type App as VueApp, createSSRApp } from 'vue';
import type { Router } from 'vue-router';
import * as Sentry from '@sentry/core';
import type { VueHeadClient } from '@unhead/vue';
import pino from 'pino';

import { initMatomo } from '@clovyr/bed/composables/useMatomo';
import general from '@clovyr/bed/mixins/general';
import CatalogFullJSON from '@clovyr/garden/dist/garden/catalog-full.json?raw';
import type { Catalog } from '@clovyr/pollen';
import { newGarden } from '@clovyr/pollen/garden/impl';

import { usePayments } from './composables/usePayments';
import { addComponents } from './init/components';
import { initConfig } from './init/config';
import { initDebug } from './init/debug';
import { initHead } from './init/head';
import { initSentry } from './init/sentry';
import { type DeviceType, useMediaQueryStore } from './stores/media_query_store';
import { usePollenStore } from './stores/pollen_store';
import { FeatureFlag, useUserFlagsStore } from './stores/user_flags';
import App from './App.vue';
import { createAppRouter } from './router';

export async function createApp(
  flags: FeatureFlag[],
  deviceType?: DeviceType,
  catalog?: Catalog,
): Promise<{
  app: VueApp<Element>;
  router: Router;
  head: VueHeadClient;
}> {
  const logger = pino();
  const app = createSSRApp(App);
  const router = createAppRouter();
  const pinia = createPinia();

  app.provide('url', import.meta.env.SSR ? null : window.location.href);

  if (deviceType) {
    const mediaQueryStore = useMediaQueryStore(pinia);
    mediaQueryStore.setDeviceType(deviceType);
  }

  const userFlagStore = useUserFlagsStore(pinia);
  userFlagStore.setFlags(flags);

  app.config.errorHandler = (err) => {
    // global error handler for uncaught exceptions
    // better local logging than sentry (includes stack)
    let msg = `caught vue err: ${err}`;
    if ((err as Error).stack) {
      msg += `\n${(err as Error).stack}`;
    }
    logger.error(msg);
  };

  addComponents(app);
  // initFormbricks();
  await initConfig().then(async (cfg) => {
    // init items which require config to load but should not block app startup
    initSentry(app, router);
    if (!import.meta.env.SSR) {
      setTimeout(() => {
        // load analytics after a short delay to avoid blocking app startup
        let matomoSiteID = 0;
        if (import.meta.env.VITE_MATOMO_SITE_ID) {
          matomoSiteID = parseInt(import.meta.env.VITE_MATOMO_SITE_ID, 10);
        } else if (cfg.VITE_APP_ENV === 'production') {
          matomoSiteID = 2;
        }
        if (matomoSiteID > 0) {
          void initMatomo(app, router, matomoSiteID);
        }
      }, 100);

      // prevent dropped files from opening in the browser
      window.addEventListener(
        'dragover',
        (e) => {
          e.preventDefault();
        },
        false,
      );

      window.addEventListener(
        'drop',
        (e) => {
          e.preventDefault();
        },
        false,
      );
    }

    // set garden instance now that config is available
    const garden = newGarden(
      cfg.VITE_GARDEN_URL,
      userFlagStore.userHasFeatureFlag(FeatureFlag.Internal),
    );

    // add publisher platform apps
    let location: string | Catalog = `${cfg.VITE_PUBLIC_URL}/api/garden/catalog`;
    if (import.meta.env.SSR && catalog) {
      location = catalog;
    } else if (!import.meta.env.SSR && window?.clovyr?.catalog) {
      // use catalog sent along with html
      location = window.clovyr.catalog as Catalog;
    }
    await garden.addIndex(location).catch((e) => {
      logger.error(
        `failed to add garden index ${typeof location === 'string' ? location : location.id}: ${e}`,
      );
      Sentry.captureException(e);
    });

    // In dev mode, slurp all app catalogs from CatalogFullJSON and update the garden
    if (import.meta.env.DEV) {
      try {
        const catalogFull = JSON.parse(CatalogFullJSON);
        garden.getApps().forEach((app) => {
          const appID = app.metadata.id;
          if (catalogFull.apps && Array.isArray(catalogFull.apps)) {
            const full = catalogFull.apps.find((a: any) => a.metadata.id === appID);
            if (full) {
              app.deployment.methods.forEach((method) => {
                app.deployment.contents ||= {};
                app.deployment.contents[method] = full.deployment.contents[method];
              });
            }
          }
        });
      } catch (e) {
        logger.error(`failed to slurp dev app catalogs: ${e}`);
      }
    }

    const pollenStore = usePollenStore(pinia);
    await pollenStore.pollen.init();
    pollenStore.setGarden(garden);

    if (typeof localStorage !== 'undefined') {
      void pollenStore.migrateCodeDeployments();
    }

    // immediately load global data
    if (
      !import.meta.env.SSR &&
      (pollenStore.isUserAuthenticated || pollenStore.deployments.length > 0)
    ) {
      void usePayments().refreshSubscription();
      void pollenStore.refreshPasskeys();
    }
  });

  app.use(pinia);
  app.use(router);
  const head = initHead(app);

  app.mixin(general);

  initDebug(pinia, router);

  return { app, router, head };
}
